<script>
import { createBrand, deleteBrand, getBrandList, toggleBrandActive, updateBrand } from '@/api/brand.js'
import { getAdminInfo } from '@/utils/token.js'
import AppPagination from '@/components/common/AppPagination.vue'

export default {
  name: 'BrandListPage',
  components: { AppPagination },
  data() {
    return {
      search: { keyword: '', activeYn: '' },
      activeOptions: [
        { title: '전체', value: '' },
        { title: '사용', value: 'Y' },
        { title: '미사용', value: 'N' },
      ],
      items: [],
      page: 1,
      size: 10,
      total: 0,
      loading: false,
      saving: false,
      dialog: false,
      editTarget: null,
      form: { brandName: '', brandDesc: '', manufacturer: '', origin: '', sortOrder: 0 },
      adminInfo: {},
    }
  },
  computed: {
    isHq() {
      return this.adminInfo.adminType === 'HQ'
    },
    dialogTitle() {
      return this.editTarget ? '브랜드 수정' : '브랜드 등록'
    },
  },
  mounted() {
    this.adminInfo = getAdminInfo()
    this.fetchList()
  },
  methods: {
    async fetchList() {
      this.loading = true
      try {
        const res = await getBrandList({
          keyword: this.search.keyword || undefined,
          activeYn: this.search.activeYn || undefined,
          page: this.page,
          size: this.size,
        })
        if (res.data?.code === 0) {
          this.items = res.data.data?.items || []
          this.total = res.data.data?.total || 0
        }
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.page = 1
      this.fetchList()
    },
    handleReset() {
      this.search = { keyword: '', activeYn: '' }
      this.page = 1
      this.fetchList()
    },
    openCreate() {
      this.editTarget = null
      this.form = { brandName: '', brandDesc: '', manufacturer: '', origin: '', sortOrder: 0 }
      this.dialog = true
    },
    openEdit(item) {
      this.editTarget = item
      this.form = {
        brandName: item.brandName || '',
        brandDesc: item.brandDesc || '',
        manufacturer: item.manufacturer || '',
        origin: item.origin || '',
        sortOrder: item.sortOrder ?? 0,
      }
      this.dialog = true
    },
    async saveBrand() {
      if (!this.form.brandName?.trim()) return
      this.saving = true
      try {
        const payload = { ...this.form, brandName: this.form.brandName.trim() }
        const res = this.editTarget
          ? await updateBrand(this.editTarget.brandCode, payload)
          : await createBrand(payload)
        if (res.data?.code === 0) {
          this.dialog = false
          this.fetchList()
        }
      } finally {
        this.saving = false
      }
    },
    async toggleActive(item) {
      if (!this.isHq) return
      const res = await toggleBrandActive(item.brandCode)
      if (res.data?.code === 0) this.fetchList()
    },
    async removeBrand(item) {
      if (!this.isHq) return
      if (!window.confirm(`${item.brandName} 브랜드를 삭제하시겠습니까?`)) return
      const res = await deleteBrand(item.brandCode)
      if (res.data?.code === 0) this.fetchList()
      else alert(res.data?.message || '삭제할 수 없습니다.')
    },
    formatDate(dt) {
      return dt ? String(dt).substring(0, 10) : '-'
    },
  },
}
</script>

<template>
  <div>
    <div class="d-flex align-center justify-space-between mb-5">
      <div>
        <div class="text-h5 font-weight-bold">브랜드 관리</div>
        <div class="text-body-2 text-grey mt-1">상품 브랜드 조회 및 등록</div>
      </div>
      <v-btn v-if="isHq" color="primary" prepend-icon="mdi-plus" @click="openCreate">브랜드 등록</v-btn>
    </div>

    <v-card rounded="lg" elevation="1" class="mb-5">
      <v-card-text>
        <v-row class="align-center">
          <v-col cols="12" md="5">
            <v-text-field v-model="search.keyword" label="브랜드 검색" placeholder="브랜드명, 제조사, 원산지" variant="outlined" density="compact" hide-details clearable @keyup.enter="handleSearch" />
          </v-col>
          <v-col cols="12" md="3">
            <v-select v-model="search.activeYn" :items="activeOptions" label="사용여부" variant="outlined" density="compact" hide-details />
          </v-col>
          <v-col cols="12" md="4" class="d-flex justify-end ga-2 search-actions">
            <v-btn color="primary" prepend-icon="mdi-magnify" @click="handleSearch">검색</v-btn>
            <v-btn variant="outlined" prepend-icon="mdi-refresh" @click="handleReset">초기화</v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card rounded="lg" elevation="1">
      <v-card-title class="text-subtitle-1 font-weight-bold">
        브랜드 목록
        <v-chip size="small" color="primary" label class="ml-2">{{ total }}건</v-chip>
      </v-card-title>
      <v-progress-linear v-if="loading" indeterminate color="primary" />
      <v-table density="compact">
        <thead>
          <tr>
            <th>노출순위</th>
            <th>브랜드코드</th>
            <th>브랜드명</th>
            <th>제조사</th>
            <th>원산지</th>
            <th>사용여부</th>
            <th>등록일</th>
            <th v-if="isHq" class="text-end">관리</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in items" :key="item.brandCode">
            <td class="text-caption">{{ item.sortOrder ?? 0 }}</td>
            <td class="text-caption">{{ item.brandCode }}</td>
            <td class="font-weight-medium">{{ item.brandName }}</td>
            <td>{{ item.manufacturer || '-' }}</td>
            <td>{{ item.origin || '-' }}</td>
            <td>
              <v-switch :model-value="item.activeYn === 'Y'" color="primary" density="compact" hide-details :disabled="!isHq" @click.stop="toggleActive(item)" />
            </td>
            <td class="text-caption">{{ formatDate(item.createdDt) }}</td>
            <td v-if="isHq" class="text-end">
              <v-btn icon="mdi-pencil" size="small" variant="text" @click="openEdit(item)" />
              <v-btn icon="mdi-delete-outline" size="small" variant="text" color="error" @click="removeBrand(item)" />
            </td>
          </tr>
          <tr v-if="!loading && !items.length">
            <td :colspan="isHq ? 8 : 7" class="text-center text-grey py-8">브랜드가 없습니다.</td>
          </tr>
        </tbody>
      </v-table>
      <div class="px-4 pb-4">
        <AppPagination :total="total" :page="page" :size="size" @update:page="p => { page = p; fetchList() }" @update:size="s => { size = s; page = 1; fetchList() }" />
      </div>
    </v-card>

    <v-dialog v-model="dialog" max-width="560">
      <v-card rounded="lg">
        <v-card-title>{{ dialogTitle }}</v-card-title>
        <v-card-text>
          <v-text-field v-model="form.brandName" label="브랜드명 *" variant="outlined" density="comfortable" />
          <!-- 작을수록 앞이다. 자사몰의 상품목록 좌측 필터와 메인 주요 브랜드가
               이 순서를 그대로 쓴다(2026-09-28). -->
          <v-text-field
            v-model.number="form.sortOrder"
            label="노출순위"
            type="number"
            hint="작을수록 앞에 나옵니다"
            persistent-hint
            variant="outlined"
            density="comfortable"
          />
          <v-text-field v-model="form.manufacturer" label="제조사" variant="outlined" density="comfortable" />
          <v-text-field v-model="form.origin" label="원산지" variant="outlined" density="comfortable" />
          <v-textarea v-model="form.brandDesc" label="설명" variant="outlined" density="comfortable" rows="3" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="dialog = false">취소</v-btn>
          <v-btn color="primary" :loading="saving" @click="saveBrand">저장</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
@media (max-width: 959px) {
  .search-actions {
    justify-content: flex-start !important;
  }
}
</style>
